import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { slugify } from '@/lib/slug';
import type { Language } from '@/lib/api';

interface Props {
  open: boolean;
  pending: boolean;
  availableLanguages: Language[];
  onClose: () => void;
  onSubmit: (input: {
    name: string;
    slug: string;
    sourceLanguageCode: string;
    targetLanguageCodes?: string[];
    defaultFormat?: 'po' | 'pot' | 'json';
  }) => Promise<void> | void;
}

export function CreateProjectDialog({
  open,
  pending,
  availableLanguages,
  onClose,
  onSubmit,
}: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [sourceLanguageCode, setSource] = useState('');
  const [targets, setTargets] = useState<string[]>([]);
  const [defaultFormat, setFormat] = useState<'po' | 'pot' | 'json'>('po');

  useEffect(() => {
    if (open) {
      setName('');
      setSlug('');
      setSlugTouched(false);
      setSource(availableLanguages[0]?.code ?? '');
      setTargets([]);
      setFormat('po');
    }
  }, [open, availableLanguages]);

  const targetable = useMemo(
    () => availableLanguages.filter((l) => l.code !== sourceLanguageCode),
    [availableLanguages, sourceLanguageCode],
  );

  function onNameChange(value: string) {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  }

  function toggleTarget(code: string) {
    setTargets((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));
  }

  const canSubmit =
    name.trim().length >= 2 &&
    slug.trim().length >= 2 &&
    sourceLanguageCode !== '' &&
    !pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="New project"
      description="Projects host translation keys and pick a source + target languages from the workspace pool."
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              void onSubmit({
                name: name.trim(),
                slug: slug.trim(),
                sourceLanguageCode,
                targetLanguageCodes: targets.length > 0 ? targets : undefined,
                defaultFormat,
              })
            }
            disabled={!canSubmit}
          >
            {pending ? 'Creating…' : 'Create project'}
          </Button>
        </>
      }
    >
      {availableLanguages.length === 0 ? (
        <p className="rounded-md border border-warning/40 bg-warning/10 p-3 text-xs text-warning">
          Add at least one language to the workspace before creating a project.
        </p>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) {
              void onSubmit({
                name: name.trim(),
                slug: slug.trim(),
                sourceLanguageCode,
                targetLanguageCodes: targets.length > 0 ? targets : undefined,
                defaultFormat,
              });
            }
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="proj-name">Name</Label>
              <Input
                id="proj-name"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="CoreFi web banking"
                autoFocus
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="proj-slug">Slug</Label>
              <Input
                id="proj-slug"
                value={slug}
                onChange={(e) => {
                  setSlug(slugify(e.target.value));
                  setSlugTouched(true);
                }}
                placeholder="corefi-web"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="proj-src">Source language</Label>
              <select
                id="proj-src"
                value={sourceLanguageCode}
                onChange={(e) => setSource(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                {availableLanguages.map((lang) => (
                  <option key={lang.id} value={lang.code}>
                    {lang.code} {lang.label ? `— ${lang.label}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="proj-fmt">Default file format</Label>
              <select
                id="proj-fmt"
                value={defaultFormat}
                onChange={(e) => setFormat(e.target.value as 'po' | 'pot' | 'json')}
                className="flex h-9 w-full rounded-md border border-input bg-surface px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="po">PO (gettext)</option>
                <option value="pot">POT (template)</option>
                <option value="json">JSON</option>
              </select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Target languages</Label>
            {targetable.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                Add more languages to the workspace to pick targets.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {targetable.map((lang) => {
                  const active = targets.includes(lang.code);
                  return (
                    <button
                      key={lang.id}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleTarget(lang.code)}
                      className={
                        active
                          ? 'inline-flex items-center gap-1.5 rounded-md border border-primary bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary'
                          : 'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-medium text-foreground hover:border-primary/40'
                      }
                    >
                      <Badge tone={active ? 'primary' : 'muted'}>{lang.code}</Badge>
                      {lang.label && <span>{lang.label}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}
